import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { archiveChannelChatChannelsChannelIdArchivePost } from '../../services/api/sdk.gen';
import { useStore } from '../../store';
import { DestructiveConfirmDialog } from './DestructiveConfirmDialog';
import { groupKeys } from './useGroupAdminQueries';

type ArchiveGroupDialogProps = {
  groupName: string;
  channelId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onArchived: () => void;
};

export function ArchiveGroupDialog({
  groupName,
  channelId,
  open,
  onOpenChange,
  onArchived,
}: ArchiveGroupDialogProps) {
  const client = useQueryClient();
  const showError = useStore(state => state.showError);
  const [pending, setPending] = useState(false);

  const archive = async () => {
    setPending(true);
    try {
      await archiveChannelChatChannelsChannelIdArchivePost({
        path: { channel_id: channelId },
        throwOnError: true,
      });
      // The row shows Archived and the detail refetches as not editable, so
      // the page swaps the editor for the read-only drawer.
      await client.invalidateQueries({ queryKey: groupKeys.all });
      onArchived();
    } catch (error) {
      console.error('Failed to archive group', error);
      showError('Could not save the group');
    } finally {
      setPending(false);
    }
  };

  return (
    <DestructiveConfirmDialog
      open={open}
      title={`Archive ${groupName}?`}
      message="The group leaves the directory and every member's inbox, and nobody can send in it. This cannot be undone."
      confirmLabel='Archive'
      pending={pending}
      onConfirm={archive}
      onCancel={() => onOpenChange(false)}
    />
  );
}
